import { isWorkflowFinished } from "./constants";
import type { ProjectSummary } from "./types";

export type ReadinessState = "ready" | "attention";

export type ReadinessCheck = {
  label: string;
  state: ReadinessState;
  detail: string;
  href?: string;
};

export type ReadinessSection = {
  title: string;
  summary: string;
  checks: ReadinessCheck[];
};

export type ExportReadiness = {
  overallState: ReadinessState;
  missingCount: number;
  readyCount: number;
  previewAllowed: boolean;
  outputAllowed: boolean;
  outputBlockedReason: string;
  sections: ReadinessSection[];
};

function hasFilledValue(values: string[]): boolean {
  return values.some((value) => value.trim().length > 0);
}

function countFinishedItems(project: ProjectSummary): number {
  return project.inspectionItems.filter((item) => isWorkflowFinished(item.workflowStatus)).length;
}

function countFinishedSubitems(project: ProjectSummary): number {
  return project.inspectionItems.reduce(
    (count, item) => count + item.subitems.filter((subitem) => isWorkflowFinished(subitem.workflowStatus)).length,
    0,
  );
}

function countTotalSubitems(project: ProjectSummary): number {
  return project.inspectionItems.reduce((count, item) => count + item.subitems.length, 0);
}

export function buildExportReadiness(project: ProjectSummary, pendingSyncCount = 0): ExportReadiness {
  const filledBuildingUnits = project.overview.buildingUnits.filter((unit) =>
    [unit.buildingName, unit.structureType, unit.usageType, unit.fireResistanceLevel].some(
      (value) => value.trim().length > 0,
    ),
  );
  const finishedItemCount = countFinishedItems(project);
  const finishedSubitems = countFinishedSubitems(project);
  const totalItems = project.inspectionItems.length;
  const totalSubitems = countTotalSubitems(project);
  const previewAllowed = true;
  const inspectionFinished = totalItems > 0 && finishedItemCount === totalItems;
  const outputAllowed = inspectionFinished && pendingSyncCount === 0;

  const sections: ReadinessSection[] = [
    {
      title: "项目信息",
      summary: "这部分决定项目名称、地址、工程类别、建设性质和参建单位是否已经具备出报告基础。",
      checks: [
        {
          label: "工程类别",
          state: project.basicInfo.projectCategory.trim() ? "ready" : "attention",
          detail: project.basicInfo.projectCategory.trim()
            ? `已选择：${project.basicInfo.projectCategory}。`
            : "还没有选择“特殊建设工程 / 其他建设工程”。",
          href: `#/projects/basic-info?id=${project.id}`,
        },
        {
          label: "项目名称与地址",
          state:
            project.basicInfo.projectName.trim() && project.basicInfo.projectAddress.trim()
              ? "ready"
              : "attention",
          detail:
            project.basicInfo.projectName.trim() && project.basicInfo.projectAddress.trim()
              ? "项目名称和工程地址都已经填写。"
              : "项目名称或工程地址仍有缺失。",
          href: `#/projects/basic-info?id=${project.id}`,
        },
        {
          label: "建设性质",
          state: project.basicInfo.constructionNature.length ? "ready" : "attention",
          detail: project.basicInfo.constructionNature.length
            ? `已选择：${project.basicInfo.constructionNature.join("、")}。`
            : "建设性质还没有勾选。",
          href: `#/projects/basic-info?id=${project.id}`,
        },
        {
          label: "参建单位",
          state:
            hasFilledValue(project.basicInfo.constructionUnits) &&
            hasFilledValue(project.basicInfo.designCompanies) &&
            hasFilledValue(project.basicInfo.constructionCompanies)
              ? "ready"
              : "attention",
          detail:
            hasFilledValue(project.basicInfo.constructionUnits) &&
            hasFilledValue(project.basicInfo.designCompanies) &&
            hasFilledValue(project.basicInfo.constructionCompanies)
              ? "建设单位、设计企业、施工企业都已有内容。"
              : "建设单位、设计企业、施工企业至少还有一项未补充完整。",
          href: `#/projects/basic-info?id=${project.id}`,
        },
      ],
    },
    {
      title: "项目概况",
      summary: "这部分决定报告“一、项目概况”的建筑单体、装修装饰、建筑保温和消防设施描述。",
      checks: [
        {
          label: "建筑单体信息",
          state: filledBuildingUnits.length ? "ready" : "attention",
          detail: filledBuildingUnits.length
            ? `当前已录入 ${filledBuildingUnits.length} 个建筑单体。`
            : "还没有录入建筑单体信息。",
          href: `#/projects/basic-info?id=${project.id}`,
        },
        {
          label: "消防设施勾选",
          state: project.overview.fireFacilitySystems.length ? "ready" : "attention",
          detail: project.overview.fireFacilitySystems.length
            ? `已勾选 ${project.overview.fireFacilitySystems.length} 项消防设施。`
            : "还没有勾选“本工程设有……等消防设施”。",
          href: `#/projects/basic-info?id=${project.id}`,
        },
      ],
    },
    {
      title: "接件与现场评定",
      summary: "这部分决定报告正文和现场评定记录表是否足够完整。",
      checks: [
        {
          label: "接件正文",
          state: project.receiving.documentBody.trim() ? "ready" : "attention",
          detail: project.receiving.documentBody.trim()
            ? "接件正文已填写。"
            : "接件正文还没有填写。",
          href: `#/projects/receiving?id=${project.id}`,
        },
        {
          label: "现场评定完成状态",
          state: inspectionFinished ? "ready" : "attention",
          detail:
            totalItems > 0
              ? `已完成 ${finishedItemCount}/${totalItems} 个单项；子项完成 ${finishedSubitems}/${totalSubitems}。`
              : "当前还没有现场评定单项数据。",
          href: `#/projects/inspection-items?id=${project.id}`,
        },
        {
          label: "本地待同步提示",
          state: pendingSyncCount === 0 ? "ready" : "attention",
          detail:
            pendingSyncCount === 0
              ? "当前没有待同步内容。"
              : `当前还有 ${pendingSyncCount} 项待同步内容，先完成同步再生成与发送。`,
          href: `#/projects/home?id=${project.id}`,
        },
      ],
    },
  ];

  const allChecks = sections.flatMap((section) => section.checks);
  const readyCount = allChecks.filter((check) => check.state === "ready").length;
  const missingCount = allChecks.length - readyCount;

  return {
    overallState: missingCount === 0 ? "ready" : "attention",
    missingCount,
    readyCount,
    previewAllowed,
    outputAllowed,
    outputBlockedReason: outputAllowed
      ? "当前已满足生成与发送条件。"
      : !inspectionFinished
        ? "请先把全部单项推进到“已完成”或“不涉及”，再继续生成和发送。"
        : `当前还有 ${pendingSyncCount} 条待同步内容，请先完成同步，再继续生成和发送。`,
    sections,
  };
}
